/**
 * NFL team visual identity — trademark-safe by design.
 *
 * Product decision (docs/NFL-DATA-PROVIDERS.md §1): we display only the CITY
 * name plus primary team colors on a generic helmet silhouette — no team
 * nicknames, no logos, no wordmarks. Color palettes themselves are not
 * protectable; the helmet art is our own generic shape.
 *
 * displayName is what gets seeded into UndercardGame.homeTeam/awayTeam and is
 * the lookup key for UI visuals. The two-team cities (New York, Los Angeles)
 * get a letter disambiguator, Yahoo-style.
 */

export interface NflTeamVisual {
  abbr: string;
  city: string;
  /** City-based display name; unique across the league. */
  displayName: string;
  /** Helmet shell color. */
  primary: string;
  /** Stripe/facemask accent color. */
  secondary: string;
}

export const NFL_TEAMS: Record<string, NflTeamVisual> = {
  ARI: { abbr: 'ARI', city: 'Arizona',       displayName: 'Arizona',          primary: '#97233F', secondary: '#FFB612' },
  ATL: { abbr: 'ATL', city: 'Atlanta',       displayName: 'Atlanta',          primary: '#A71930', secondary: '#A5ACAF' },
  BAL: { abbr: 'BAL', city: 'Baltimore',     displayName: 'Baltimore',        primary: '#241773', secondary: '#9E7C0C' },
  BUF: { abbr: 'BUF', city: 'Buffalo',       displayName: 'Buffalo',          primary: '#00338D', secondary: '#C60C30' },
  CAR: { abbr: 'CAR', city: 'Carolina',      displayName: 'Carolina',         primary: '#0085CA', secondary: '#101820' },
  CHI: { abbr: 'CHI', city: 'Chicago',       displayName: 'Chicago',          primary: '#0B162A', secondary: '#C83803' },
  CIN: { abbr: 'CIN', city: 'Cincinnati',    displayName: 'Cincinnati',       primary: '#FB4F14', secondary: '#101820' },
  CLE: { abbr: 'CLE', city: 'Cleveland',     displayName: 'Cleveland',        primary: '#311D00', secondary: '#FF3C00' },
  DAL: { abbr: 'DAL', city: 'Dallas',        displayName: 'Dallas',           primary: '#003594', secondary: '#869397' },
  DEN: { abbr: 'DEN', city: 'Denver',        displayName: 'Denver',           primary: '#FB4F14', secondary: '#002244' },
  DET: { abbr: 'DET', city: 'Detroit',       displayName: 'Detroit',          primary: '#0076B6', secondary: '#B0B7BC' },
  GB:  { abbr: 'GB',  city: 'Green Bay',     displayName: 'Green Bay',        primary: '#203731', secondary: '#FFB612' },
  HOU: { abbr: 'HOU', city: 'Houston',       displayName: 'Houston',          primary: '#03202F', secondary: '#A71930' },
  IND: { abbr: 'IND', city: 'Indianapolis',  displayName: 'Indianapolis',     primary: '#002C5F', secondary: '#A2AAAD' },
  JAX: { abbr: 'JAX', city: 'Jacksonville',  displayName: 'Jacksonville',     primary: '#101820', secondary: '#D7A22A' },
  KC:  { abbr: 'KC',  city: 'Kansas City',   displayName: 'Kansas City',      primary: '#E31837', secondary: '#FFB81C' },
  LAC: { abbr: 'LAC', city: 'Los Angeles',   displayName: 'Los Angeles (C)',  primary: '#0080C6', secondary: '#FFC20E' },
  LAR: { abbr: 'LAR', city: 'Los Angeles',   displayName: 'Los Angeles (R)',  primary: '#003594', secondary: '#FFA300' },
  LV:  { abbr: 'LV',  city: 'Las Vegas',     displayName: 'Las Vegas',        primary: '#101820', secondary: '#A5ACAF' },
  MIA: { abbr: 'MIA', city: 'Miami',         displayName: 'Miami',            primary: '#008E97', secondary: '#FC4C02' },
  MIN: { abbr: 'MIN', city: 'Minnesota',     displayName: 'Minnesota',        primary: '#4F2683', secondary: '#FFC62F' },
  NE:  { abbr: 'NE',  city: 'New England',   displayName: 'New England',      primary: '#002244', secondary: '#C60C30' },
  NO:  { abbr: 'NO',  city: 'New Orleans',   displayName: 'New Orleans',      primary: '#101820', secondary: '#D3BC8D' },
  NYG: { abbr: 'NYG', city: 'New York',      displayName: 'New York (G)',     primary: '#0B2265', secondary: '#A71930' },
  NYJ: { abbr: 'NYJ', city: 'New York',      displayName: 'New York (J)',     primary: '#125740', secondary: '#FFFFFF' },
  PHI: { abbr: 'PHI', city: 'Philadelphia',  displayName: 'Philadelphia',     primary: '#004C54', secondary: '#A5ACAF' },
  PIT: { abbr: 'PIT', city: 'Pittsburgh',    displayName: 'Pittsburgh',       primary: '#101820', secondary: '#FFB612' },
  SEA: { abbr: 'SEA', city: 'Seattle',       displayName: 'Seattle',          primary: '#002244', secondary: '#69BE28' },
  SF:  { abbr: 'SF',  city: 'San Francisco', displayName: 'San Francisco',    primary: '#AA0000', secondary: '#B3995D' },
  TB:  { abbr: 'TB',  city: 'Tampa Bay',     displayName: 'Tampa Bay',        primary: '#D50A0A', secondary: '#FF7900' },
  TEN: { abbr: 'TEN', city: 'Tennessee',     displayName: 'Tennessee',        primary: '#0C2340', secondary: '#4B92DB' },
  WAS: { abbr: 'WAS', city: 'Washington',    displayName: 'Washington',       primary: '#5A1414', secondary: '#FFB612' },
};

const BY_DISPLAY_NAME: Record<string, NflTeamVisual> = Object.fromEntries(
  Object.values(NFL_TEAMS).map((t) => [t.displayName.toLowerCase(), t])
);

/**
 * Resolve an NFL team from a game's stored team string (displayName) or an
 * abbreviation. Returns null for non-NFL names so callers can fall through
 * to other sports' rendering (country flags, plain text).
 */
export function getNflTeam(name: string | null | undefined): NflTeamVisual | null {
  if (!name) return null;
  const key = name.trim();
  return NFL_TEAMS[key.toUpperCase()] ?? BY_DISPLAY_NAME[key.toLowerCase()] ?? null;
}
